import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  memo,
} from "react";

// Import UI components from lift-ui
import { 
  Badge,
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger,
  Progress,
  cn,
  Icon,
  SingleSelect,
} from "lift-ui";
import { DynamicCard, type TrendConfig, type ValueType } from "@/components/ui/card";
import {
  AdminAnalyticsV2Response,
  AdminFilteredSessionsV2Response,
  DistinctBrandIdsV2Response,
} from "../../api";
import {
  TimeSeriesAnalytics,
  TrendsAnalytics,
  DetailedAnalytics,
} from "../../hooks/useAdminData";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
// Icons from lucide-react (lift-ui Icon component requires runtime SVG loading)
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  MessageCircle,
  ThumbsUp,
  ThumbsDown,
  Users,
  Building,
  Activity,
  Target,
  Zap,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Calendar,
  Minus,
  Hash,
  List,
  Star,
  Award,
  Brain,
  Globe,
  Monitor,
} from "lucide-react";
import { useChatV2 } from "../../context/ChatV2Context";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Lift-UI aligned color palette for visualizations
const CHART_COLORS = {
  primary: "#334238",      // Lift-UI dark green
  success: "#22c55e",      // Green
  danger: "#ef4444",       // Red
  warning: "#f59e0b",      // Amber
  muted: "#585755",        // Lift-UI muted text
  purple: "#8b5cf6",       // Purple (kept for variety)
  cyan: "#06b6d4",         // Cyan
  emerald: "#334238",      // Lift-UI dark green
  rose: "#f43f5e",         // Rose (kept for variety)
  indigo: "#334238",       // Lift-UI dark green  
  orange: "#f97316",       // Orange
  teal: "#14b8a6",         // Teal
  slate: "#585755",        // Lift-UI muted
  liftGreen: "#E3FF6A",    // Lift-UI electric green
  liftLightGreen: "#F4FFC6", // Lift-UI light green
};

// Stable chart options that don't change
const STABLE_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 0, // Disable animation completely
  },
  interaction: {
    mode: "index" as const,
    intersect: false,
  },
};

interface AnalyticsPanelProps {
  analytics: AdminAnalyticsV2Response | null;
  sessions: AdminFilteredSessionsV2Response | null;
  brands: DistinctBrandIdsV2Response | null;
  loading: boolean;
  compact?: boolean;
  timeSeriesAnalytics?: TimeSeriesAnalytics | null;
  trendsAnalytics?: TrendsAnalytics | null;
  detailedAnalytics?: DetailedAnalytics | null;
  onTrendsPeriodChange?: (period: string) => void;
  onTimeSeriesPeriodChange?: (period: string, granularity?: string) => void;
  onUserEngagementPeriodChange?: (period: string) => void;
  onDetailedAnalyticsPeriodChange?: (period: string) => void;
  activeAnalyticsTab?: string;
  onAnalyticsTabChange?: (tab: string) => void;
}

const AnalyticsPanel: React.FC<AnalyticsPanelProps> = ({
  analytics,
  sessions,
  brands,
  loading,
  compact = false,
  timeSeriesAnalytics,
  trendsAnalytics,
  detailedAnalytics,
  onTrendsPeriodChange,
  onTimeSeriesPeriodChange,
  onUserEngagementPeriodChange,
  onDetailedAnalyticsPeriodChange,
  activeAnalyticsTab: externalActiveAnalyticsTab,
  onAnalyticsTabChange,
}) => {
  // Debug: Log when component receives props
  console.log('[AnalyticsPanel] Component received props - externalActiveAnalyticsTab:', externalActiveAnalyticsTab);
  const { brandMap } = useChatV2();

  // Local state to track current trends period
  const [currentTrendsPeriod, setCurrentTrendsPeriod] = useState("last_30d");
  
  // Local state to track current user engagement period
  const [currentUserEngagementPeriod, setCurrentUserEngagementPeriod] = useState("last_30d");
  
  // Local state to track granularity
  const [currentGranularity, setCurrentGranularity] = useState("day");
  
  // Use external activeAnalyticsTab if provided, otherwise use internal state
  const [internalActiveAnalyticsTab, setInternalActiveAnalyticsTab] = useState("overview");
  
  // IMPORTANT: Always use external prop when provided - don't use internal state if external is provided
  // This ensures we always reflect the parent's state
  const activeAnalyticsTab = externalActiveAnalyticsTab !== undefined && externalActiveAnalyticsTab !== null && externalActiveAnalyticsTab !== ""
    ? externalActiveAnalyticsTab 
    : (internalActiveAnalyticsTab || "overview");
  const setActiveAnalyticsTab = onAnalyticsTabChange || setInternalActiveAnalyticsTab;
  
  // Debug: Log when external prop changes
  useEffect(() => {
    console.log('[AnalyticsPanel] externalActiveAnalyticsTab changed:', externalActiveAnalyticsTab, 'final activeAnalyticsTab:', activeAnalyticsTab, 'internal:', internalActiveAnalyticsTab);
  }, [externalActiveAnalyticsTab, activeAnalyticsTab, internalActiveAnalyticsTab]);


  // Add state to prevent loops and track user intent
  const [isUserChangingGranularity, setIsUserChangingGranularity] = useState(false);
  const [isUserChangingPeriod, setIsUserChangingPeriod] = useState(false);

  // CRITICAL: Add unified loading state to track all API calls triggered by dropdown changes
  const [isRefreshingFromDropdown, setIsRefreshingFromDropdown] = useState(false);
  const [pendingApiCalls, setPendingApiCalls] = useState<Set<string>>(new Set());

  // Track when data was last updated to prevent stale renders during transitions
  const [lastDataUpdate, setLastDataUpdate] = useState<number>(Date.now());

  // Add state to track data stability during API transitions
  const [dataStableChecksum, setDataStableChecksum] = useState<string>('');

  // Comprehensive loading state - wait for all required data AND dropdown-triggered refreshes
  const isDataLoading = useMemo(() => {
    return (
      loading ||
      isRefreshingFromDropdown || // BLOCK all renders when dropdown changes are in progress
      pendingApiCalls.size > 0 || // CRITICAL: Also block when any API calls are pending
      !analytics ||
      !detailedAnalytics ||
      !brands ||
      !timeSeriesAnalytics ||
      !trendsAnalytics ||
      // Check if data structures have the expected properties
      !detailedAnalytics.overview ||
      !detailedAnalytics.user_engagement ||
      !detailedAnalytics.peak_usage_times ||
      !timeSeriesAnalytics.data_points ||
      !trendsAnalytics.sessions_trend
    );
  }, [
    loading,
    isRefreshingFromDropdown, // Add this to the dependency array
    pendingApiCalls.size, // CRITICAL: Track pending API calls
    analytics,
    detailedAnalytics,
    brands,
    timeSeriesAnalytics,
    trendsAnalytics,
  ]);

  // Generate data checksum to detect when all APIs have completed and data is stable
  const currentDataChecksum = useMemo(() => {
    if (isDataLoading) return '';
    
    // Create a checksum based on key data properties to detect when everything is loaded
    const checksumData = {
      analytics: analytics?.total_sessions || 0,
      timeSeriesLength: timeSeriesAnalytics?.data_points?.length || 0,
      trendsCurrentValue: trendsAnalytics?.sessions_trend?.current_value || 0,
      detailedTotalSessions: detailedAnalytics?.overview?.total_sessions || 0,
      brandsCount: brands?.total_distinct_brands || 0,
      currentPeriod: currentTrendsPeriod,
      currentGranularity: currentGranularity,
    };
    return JSON.stringify(checksumData);
  }, [
    isDataLoading,
    analytics?.total_sessions,
    timeSeriesAnalytics?.data_points?.length,
    trendsAnalytics?.sessions_trend?.current_value,
    detailedAnalytics?.overview?.total_sessions,
    brands?.total_distinct_brands,
    currentTrendsPeriod,
    currentGranularity,
  ]);

  // Update stable checksum only when data is stable
  useEffect(() => {
    if (!isDataLoading && currentDataChecksum && currentDataChecksum !== dataStableChecksum) {
      // Delay checksum update to ensure all state updates have settled
      const timer = setTimeout(() => {
        setDataStableChecksum(currentDataChecksum);
        setLastDataUpdate(Date.now());
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isDataLoading, currentDataChecksum, dataStableChecksum]);

  // Ensure tab state persists during loading and re-renders

  // Sync local state with trends analytics data - but don't override user changes
  useEffect(() => {
    if (trendsAnalytics?.sessions_trend?.period && !isUserChangingPeriod && !isRefreshingFromDropdown) {
      setCurrentTrendsPeriod(trendsAnalytics.sessions_trend.period);
    }
  }, [trendsAnalytics?.sessions_trend?.period, isUserChangingPeriod, isRefreshingFromDropdown]);

  // Sync local state with user engagement data - but don't override user changes
  useEffect(() => {
    if (detailedAnalytics?.user_engagement?.time_period && !isUserChangingPeriod && !isRefreshingFromDropdown) {
      setCurrentUserEngagementPeriod(detailedAnalytics.user_engagement.time_period);
    }
  }, [detailedAnalytics?.user_engagement?.time_period, isUserChangingPeriod, isRefreshingFromDropdown]);

  // Reset user change flags after a delay
  useEffect(() => {
    if (isUserChangingPeriod) {
      const timer = setTimeout(() => setIsUserChangingPeriod(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isUserChangingPeriod]);

  useEffect(() => {
    if (isUserChangingGranularity) {
      const timer = setTimeout(() => setIsUserChangingGranularity(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isUserChangingGranularity]);

  // Helper function to track API calls
  const trackApiCall = useCallback((apiName: string, promise: Promise<any>) => {
    setPendingApiCalls(prev => new Set([...prev, apiName]));
    
    return promise.finally(() => {
      setPendingApiCalls(prev => {
        const newSet = new Set(prev);
        newSet.delete(apiName);
        return newSet;
      });
    });
  }, []);

  // Monitor pending API calls and update refresh state
  useEffect(() => {
    if (pendingApiCalls.size === 0 && isRefreshingFromDropdown) {
      // All API calls completed, allow renders again after a small delay to ensure data is stable
      const timer = setTimeout(() => {
        setIsRefreshingFromDropdown(false);
      }, 200); // Increased delay to ensure all state updates settle
      return () => clearTimeout(timer);
    }
  }, [pendingApiCalls.size, isRefreshingFromDropdown]);

  // Get appropriate granularity options based on period
  const getAvailableGranularities = useCallback((period: string) => {
    switch (period) {
      case "last_24h":
        return [{ value: "hour", label: "Hourly" }];
      case "last_7d":
        return [
          { value: "hour", label: "Hourly" },
          { value: "day", label: "Daily" },
        ];
      case "last_30d":
        return [
          { value: "day", label: "Daily" },
          { value: "week", label: "Weekly" },
        ];
      case "last_3m":
      case "last_6m":
        return [
          { value: "day", label: "Daily" },
        
          { value: "week", label: "Weekly" },
          { value: "month", label: "Monthly" },
        ];
      case "last_1y":
        return [{ value: "month", label: "Monthly" }];
      default:
        return [{ value: "day", label: "Daily" }];
    }
  }, []);

  // Common period change handler - ENHANCED to track all API calls with error handling
  const handleCommonPeriodChange = useCallback((value: string) => {
    // Start the refresh process - this will block all chart renders
    setIsRefreshingFromDropdown(true);
    
    try {
      // Batch state updates to prevent multiple renders
      React.startTransition(() => {
        setIsUserChangingPeriod(true);
        setCurrentTrendsPeriod(value);
        setCurrentUserEngagementPeriod(value);
        
        // Check if current granularity is valid for the new period
        const availableGranularities = getAvailableGranularities(value);
        const isCurrentGranularityValid = availableGranularities.some(
          (option) => option.value === currentGranularity
        );
        
        // Only change granularity if current one is not valid for the new period
        let finalGranularity = currentGranularity;
        if (!isCurrentGranularityValid) {
          finalGranularity = availableGranularities[0]?.value || "day";
          setCurrentGranularity(finalGranularity);
        }
        
        // Track all API calls to ensure we wait for ALL to complete
        const apiPromises: Promise<any>[] = [];
        
        // Wrap each API call in a try-catch to prevent crashes
        // 1. Trends API: admin/v2/analytics/trends?compare_period=period
        if (onTrendsPeriodChange) {
          try {
            const trendsPromise = Promise.resolve(onTrendsPeriodChange(value)).catch(err => {
              console.error('[AnalyticsPanel] Trends API error:', err);
              return null;
            });
            apiPromises.push(trackApiCall('trends', trendsPromise));
          } catch (err) {
            console.error('[AnalyticsPanel] Error calling trends:', err);
          }
        }
        
        // 2. Time Series API: admin/v2/timeseries?time_range=period&granularity=granularity
        if (onTimeSeriesPeriodChange) {
          try {
            const timeSeriesPromise = Promise.resolve(onTimeSeriesPeriodChange(value, finalGranularity)).catch(err => {
              console.error('[AnalyticsPanel] Time series API error:', err);
              return null;
            });
            apiPromises.push(trackApiCall('timeSeries', timeSeriesPromise));
          } catch (err) {
            console.error('[AnalyticsPanel] Error calling time series:', err);
          }
        }
        
        // 3. User Engagement API: admin/v2/analytics/detailed?time_range=period
        if (onUserEngagementPeriodChange) {
          try {
            const userEngagementPromise = Promise.resolve(onUserEngagementPeriodChange(value)).catch(err => {
              console.error('[AnalyticsPanel] User engagement API error:', err);
              return null;
            });
            apiPromises.push(trackApiCall('userEngagement', userEngagementPromise));
          } catch (err) {
            console.error('[AnalyticsPanel] Error calling user engagement:', err);
          }
        }
        
        // 4. Detailed Analytics API: admin/v2/analytics/detailed?time_range=period
        if (onDetailedAnalyticsPeriodChange) {
          try {
            const detailedPromise = Promise.resolve(onDetailedAnalyticsPeriodChange(value)).catch(err => {
              console.error('[AnalyticsPanel] Detailed analytics API error:', err);
              return null;
            });
            apiPromises.push(trackApiCall('detailed', detailedPromise));
          } catch (err) {
            console.error('[AnalyticsPanel] Error calling detailed analytics:', err);
          }
        }

        // If no API calls were made, immediately stop the refresh state
        if (apiPromises.length === 0) {
          setIsRefreshingFromDropdown(false);
        }
      });
    } catch (err) {
      console.error('[AnalyticsPanel] Error in handleCommonPeriodChange:', err);
      setIsRefreshingFromDropdown(false);
    }
  }, [
    onTrendsPeriodChange, 
    onTimeSeriesPeriodChange, 
    onUserEngagementPeriodChange, 
    onDetailedAnalyticsPeriodChange, 
    getAvailableGranularities,
    currentGranularity,
    trackApiCall
  ]);

  // Granularity change handler - ENHANCED to track API calls
  const handleGranularityChange = useCallback((value: string) => {
    // Start the refresh process
    setIsRefreshingFromDropdown(true);
    
    React.startTransition(() => {
      setIsUserChangingGranularity(true);
      setCurrentGranularity(value);
    });
  }, []);

  // Debounced effect for granularity changes - ENHANCED with error handling
  useEffect(() => {
    if (isUserChangingGranularity) {
      const timeoutId = setTimeout(() => {
        if (onTimeSeriesPeriodChange) {
          try {
            // Track the time series API call with error handling
            const timeSeriesPromise = Promise.resolve(onTimeSeriesPeriodChange(currentTrendsPeriod, currentGranularity)).catch(err => {
              console.error('[AnalyticsPanel] Granularity change API error:', err);
              return null;
            });
            trackApiCall('granularityChange', timeSeriesPromise);
          } catch (err) {
            console.error('[AnalyticsPanel] Error in granularity change:', err);
            setIsRefreshingFromDropdown(false);
          }
        } else {
          // If no API call is made, stop the refresh state
          setIsRefreshingFromDropdown(false);
        }
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [currentGranularity, isUserChangingGranularity, onTimeSeriesPeriodChange, currentTrendsPeriod, trackApiCall]);

  // Trend period options - stable reference
  const trendPeriodOptions = useMemo(() => [
    { value: "last_24h", label: "Last 24 Hours" },
    { value: "last_7d", label: "Last 7 Days" },
    { value: "last_30d", label: "Last 30 Days" },
    { value: "last_3m", label: "Last 3 Months" },
    { value: "last_6m", label: "Last 6 Months" },
    { value: "last_1y", label: "Last 1 Year" },
  ], []);

  // Granularity options - stable reference
  const granularityOptions = useMemo(() => [
    { value: "hour", label: "Hourly" },
    { value: "day", label: "Daily" },
    { value: "week", label: "Weekly" },
    { value: "month", label: "Monthly" },
  ], []);

  // Enhanced memoized chart data with stability check to prevent recalculation during staggered API returns
  const timeSeriesChartData = useMemo(() => {
    // CRITICAL: Only recalculate chart data when data is stable (no pending API calls)
    if (isDataLoading || 
        !timeSeriesAnalytics?.data_points || 
        timeSeriesAnalytics.data_points.length === 0 ||
        !dataStableChecksum) {
      return null;
    }

    // Additional validation: ensure data matches current period/granularity settings
    const expectedGranularity = timeSeriesAnalytics.granularity || currentGranularity;
    if (expectedGranularity !== currentGranularity) {
      return null; // Data doesn't match current selection, wait for fresh data
    }

    return {
      labels: timeSeriesAnalytics.data_points.map((point) => {
        const date = new Date(point.timestamp);
        switch (currentGranularity) {
          case "hour":
            return date.toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
          case "week":
            return `Week ${date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}`;
          case "month":
            return date.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            });
          default:
            return date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
        }
      }),
      datasets: [
        {
          label: "Sessions",
          data: timeSeriesAnalytics.data_points.map(
            (point) => point.session_count
          ),
          borderColor: CHART_COLORS.primary,
          backgroundColor: `${CHART_COLORS.primary}20`,
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: "Unique Users",
          data: timeSeriesAnalytics.data_points.map(
            (point) => point.unique_users
          ),
          borderColor: CHART_COLORS.success,
          backgroundColor: `${CHART_COLORS.success}20`,
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  }, [
    isDataLoading, // Include loading state
    timeSeriesAnalytics?.data_points, 
    timeSeriesAnalytics?.granularity, // Include granularity from API response
    currentGranularity,
    dataStableChecksum, // CRITICAL: Only recalculate when data is stable
  ]);

  // Memoized chart options to prevent recreation
  const timeSeriesChartOptions = useMemo(() => ({
    ...STABLE_CHART_OPTIONS,
    animation: {
      duration: 0, // Disable animation completely
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: "#E5E1DF" },
        ticks: { color: "#585755" },
        title: {
          display: true,
          text: "Count",
          color: "#585755",
          font: { size: 12, weight: 500 },
        },
      },
      x: {
        grid: { display: false },
        ticks: { 
          color: "#585755",
          maxTicksLimit: currentGranularity === "hour" ? 12 : 
                        currentGranularity === "day" ? 15 : 
                        currentGranularity === "week" ? 10 : 8,
          maxRotation: currentGranularity === "hour" ? 45 : 0,
        },
        title: {
          display: true,
          text: currentGranularity === "hour" ? "Time" :
                currentGranularity === "day" ? "Date" :
                currentGranularity === "week" ? "Week" : "Month",
          color: "#585755",
          font: { size: 12, weight: 500 },
        },
      },
    },
    plugins: {
      legend: {
        position: "top" as const,
        labels: { usePointStyle: true, boxWidth: 6, color: "#585755" },
      },
      tooltip: {
        backgroundColor: "rgba(51, 66, 56, 0.95)",
        titleColor: "white",
        bodyColor: "white",
        borderColor: CHART_COLORS.primary,
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          title: (context: any) => {
            if (!timeSeriesAnalytics?.data_points) return '';
            const dataPoint = timeSeriesAnalytics.data_points[context[0].dataIndex];
            const date = new Date(dataPoint.timestamp);
            
            switch (currentGranularity) {
              case "hour":
                return date.toLocaleString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                });
              case "day":
                return date.toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long", 
                  day: "numeric",
                });
              case "week":
                const endDate = new Date(date);
                endDate.setDate(endDate.getDate() + 6);
                return `Week: ${date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })} - ${endDate.toLocaleDateString("en-US", {
                  month: "short", 
                  day: "numeric",
                  year: "numeric",
                })}`;
              case "month":
                return date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                });
              default:
                return date.toLocaleDateString();
            }
          },
          label: (context: any) => {
            if (!timeSeriesAnalytics?.data_points) return '';
            const dataPoint = timeSeriesAnalytics.data_points[context.dataIndex];
            const label = context.dataset.label;
            const value = context.parsed.y;
            
            if (label === "Sessions") {
              return [
                `Sessions: ${value}`,
                `Total Messages: ${dataPoint.message_count || 0}`,
                `Avg per Session: ${dataPoint.message_count && value > 0 ? (dataPoint.message_count / value).toFixed(1) : 0}`,
              ];
            } else {
              return [
                `Unique Users: ${value}`,
                `Sessions per User: ${value > 0 ? (timeSeriesAnalytics.data_points[context.dataIndex].session_count / value).toFixed(1) : 0}`,
              ];
            }
          },
        },
      },
    },
  }), [currentGranularity, timeSeriesAnalytics?.data_points]);

  // Memoized hourly usage chart data
  const hourlyUsageChartData = useMemo(() => {
    if (!detailedAnalytics?.peak_usage_times) return null;

    const hourlyData = Array.from({ length: 24 }, (_, hour) => {
      const hourStr = hour.toString().padStart(2, "0") + ":00";
      const dataPoint = detailedAnalytics.peak_usage_times.find(
        (p) => {
          // Handle both number and string hour formats
          const pHour = typeof p.hour === 'number' ? 
            p.hour.toString().padStart(2, "0") + ":00" : 
            p.hour;
          return pHour === hourStr;
        }
      );
      return {
        hour: hourStr,
        session_count: dataPoint?.session_count || 0,
      };
    });

    return {
      labels: hourlyData.map((data) => data.hour),
      datasets: [
        {
          label: "Sessions",
          data: hourlyData.map((data) => data.session_count),
          backgroundColor: hourlyData.map((_, index) => {
            const maxSessions = Math.max(...hourlyData.map(d => d.session_count));
            const intensity = maxSessions > 0 ? Math.min(hourlyData[index].session_count / maxSessions, 1) : 0;
            return `${CHART_COLORS.indigo}${Math.round(30 + intensity * 70).toString(16)}`;
          }),
          borderColor: CHART_COLORS.indigo,
          borderWidth: 2,
          borderRadius: 4,
        },
      ],
    };
  }, [detailedAnalytics?.peak_usage_times]);

  // Memoized hourly usage chart options
  const hourlyUsageChartOptions = useMemo(() => ({
    ...STABLE_CHART_OPTIONS,
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: "#E5E1DF" },
        ticks: { 
          color: "#585755",
          callback: (value: any) => value + " sessions",
        },
        title: {
          display: true,
          text: "Session Count",
          color: "#585755",
          font: { size: 12, weight: 500 },
        },
      },
      x: {
        grid: { display: false },
        ticks: { 
          color: "#585755",
          maxTicksLimit: 12,
        },
        title: {
          display: true,
          text: "Hour of Day",
          color: "#585755",
          font: { size: 12, weight: 500 },
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(51, 66, 56, 0.95)",
        titleColor: "white",
        bodyColor: "white",
        borderColor: CHART_COLORS.primary,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (context: any) => {
            const hour = context[0].label;
            return `${hour} - ${hour.split(":")[0]}:59`;
          },
          label: (context: any) => {
            if (!detailedAnalytics?.peak_usage_times) return '';
            const count = context.parsed.y;
            const total = detailedAnalytics.peak_usage_times.reduce((sum, data) => sum + data.session_count, 0);
            const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : "0.0";
            return [
              `Sessions: ${count}`,
              `Percentage: ${percentage}% of daily total`,
            ];
          },
        },
      },
    },
  }), [detailedAnalytics?.peak_usage_times]);

  // Enhanced metric cards with lift-ui design - memoized using DynamicCard
  const MetricCard = memo(({
    title,
    value,
    icon: Icon,
    trend,
    trendDirection,
    color = "primary",
    description,
    size = "normal",
  }: {
    title: string;
    value: string | number;
    icon: React.ElementType;
    trend?: string;
    trendDirection?: "up" | "down" | "stable";
    color?: keyof typeof CHART_COLORS;
    description?: string;
    size?: "normal" | "compact" | "large";
  }) => {
    // Convert trend direction to DynamicCard format
    const trendConfig: TrendConfig | undefined = trend ? {
      direction: trendDirection === "up" ? "up" : trendDirection === "down" ? "down" : "neutral",
      value: trend,
      showIcon: true,
      colorScheme: trendDirection === "up" ? "success" : trendDirection === "down" ? "destructive" : "default",
    } : undefined;

    // Format value with percentage if needed
    const formattedValue = typeof value === "number" 
      ? `${value.toLocaleString()}${title === "Like Rate" ? "%" : ""}`
      : value;

    return (
      <DynamicCard
        title={title}
        value={formattedValue}
        valueType="text"
        trend={trendConfig}
        description={description}
        shadow="border"
        size={size === "compact" ? "sm" : size === "large" ? "lg" : "md"}
        headerIcon={<Icon className={cn(
              "text-[#334238]",
          size === "compact" ? "h-4 w-4" : size === "large" ? "h-6 w-6" : "h-5 w-5"
        )} />}
        showMoreIcon={false}
        className="relative overflow-hidden transition-all hover:shadow-soft"
      />
    );
  });

  // Combined Usage & Trends Dashboard - memoized
  const UsageAndTrends = memo(() => {
    // Early return if data is still loading
    if (isDataLoading) return null;
    
    if (!analytics || !detailedAnalytics) return null;

    const overview = detailedAnalytics.overview;
    // Use total_messages from detailed analytics (respects time_range filter)
    const totalMessages = overview.total_messages || 0;
    // Sessions with feedback count (for reference if needed)
    const sessionsWithFeedback = overview.sessions_with_feedback || 0;

    return (
      <div className="space-y-6 w-full">
        
        {/* Common Period Selector for entire tab */}
        <div className="flex items-center justify-between w-full gap-4">
          <div className="text-left">
            <h3 className="text-lg font-semibold text-[#242424]">Usage Analytics & Trends</h3>
            <p className="text-sm text-[#585755]">
              Comprehensive metrics for {trendPeriodOptions.find(opt => opt.value === currentTrendsPeriod)?.label || currentTrendsPeriod}
            </p>
          </div>
          
          {/* Common Period and Granularity Dropdowns with enhanced padding */}
          <div className="flex items-center gap-6 px-6 py-4 bg-[#F7F4F2]/50 rounded-xl border border-[#E5E1DF] flex-shrink-0">
            {/* Loading indicator next to dropdowns when refreshing */}
            {isRefreshingFromDropdown && (
              <div className="flex items-center gap-2 text-sm text-[#334238] mr-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="font-medium">Updating charts...</span>
              </div>
            )}
            
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-[#585755] whitespace-nowrap">Period:</span>
              <SingleSelect
                options={trendPeriodOptions}
                defaultValue={currentTrendsPeriod}
                onChange={handleCommonPeriodChange}
                disabled={isRefreshingFromDropdown}
                placeholder="Select period"
                className="w-44"
                selectClassName={cn(
                  "h-10 text-sm",
                  isRefreshingFromDropdown && "opacity-50 cursor-not-allowed"
                )}
              />
            </div>
            
            {/* Granularity Selection */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-[#585755] whitespace-nowrap">Granularity:</span>
              <SingleSelect
                options={getAvailableGranularities(currentTrendsPeriod)}
                defaultValue={currentGranularity}
                onChange={handleGranularityChange}
                disabled={isRefreshingFromDropdown}
                placeholder="Select granularity"
                className="w-36"
                selectClassName={cn(
                  "h-10 text-sm",
                  isRefreshingFromDropdown && "opacity-50 cursor-not-allowed"
                )}
              />
            </div>
          </div>
        </div>

        {/* Show loading overlay during dropdown-triggered refresh */}
        {isRefreshingFromDropdown && (
          <div className="relative">
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl border border-[#E5E1DF]">
              <div className="flex flex-col items-center gap-3 text-center p-6">
                <Loader2 className="h-8 w-8 animate-spin text-[#334238]" />
                <div>
                  <div className="text-lg font-semibold text-[#242424]">Refreshing Analytics</div>
                  <div className="text-sm text-[#585755] mt-1">
                    {pendingApiCalls.size > 0 ? (
                      <>Loading {Array.from(pendingApiCalls).join(", ")} data...</>
                    ) : (
                      "Finalizing data synchronization..."
                    )}
                  </div>
                  <div className="text-xs text-[#A09D9B] mt-2">
                    {pendingApiCalls.size > 0 ? (
                      `${pendingApiCalls.size} API call${pendingApiCalls.size > 1 ? 's' : ''} remaining`
                    ) : (
                      "Please wait while all charts are synchronized"
                    )}
                  </div>
                  
                  {/* Progress indicator */}
                  <div className="w-48 bg-[#E5E1DF] rounded-full h-2 mt-3">
                    <div 
                      className="bg-[#334238] h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ 
                        width: pendingApiCalls.size > 0 
                          ? `${Math.max(20, 100 - (pendingApiCalls.size * 20))}%` 
                          : "95%" 
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* Render a placeholder div to maintain layout */}
            <div className="min-h-[600px] bg-[#F7F4F2] rounded-xl" />
          </div>
        )}

        {/* Only render actual content when NOT refreshing */}
        {!isRefreshingFromDropdown && (
          <>
            {/* Overview Metrics */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-3 w-full">
              <DynamicCard
                title="Sessions"
                value={overview.total_sessions.toString()}
                valueType="text"
                description="conversations"
                headerIcon={<MessageCircle className="h-4 w-4 text-[#334238]" />}
                shadow="border"
                size="sm"
                showMoreIcon={false}
              />
              
              <DynamicCard
                title="Users"
                value={overview.total_users.toString()}
                valueType="text"
                description="unique"
                headerIcon={<Users className="h-4 w-4 text-[#334238]" />}
                shadow="border"
                size="sm"
                showMoreIcon={false}
              />
              
              <DynamicCard
                title="Messages"
                value={totalMessages.toString()}
                valueType="text"
                description="in period"
                headerIcon={<Activity className="h-4 w-4 text-[#334238]" />}
                shadow="border"
                size="sm"
                showMoreIcon={false}
              />
              
              <DynamicCard
                title="Avg Length"
                value={overview.avg_session_length.toFixed(1)}
                valueType="text"
                description="messages/session"
                headerIcon={<Clock className="h-4 w-4 text-[#334238]" />}
                shadow="border"
                size="sm"
                showMoreIcon={false}
              />
                    </div>

            {/* Time Series Chart */}
            {timeSeriesAnalytics && timeSeriesChartData && (
              <DynamicCard
                title="Sessions & Users Over Time"
                headerIcon={<Activity className="h-5 w-5 text-[#334238]" />}
                shadow="border"
                showMoreIcon={false}
                customContent={
                  <div className="h-64 w-full">
                    <Line
                      key={`chart-${dataStableChecksum}`}
                      data={timeSeriesChartData}
                      options={timeSeriesChartOptions}
                    />
                  </div>
                }
                className="w-full"
              />
            )}

            {/* Trend Comparison */}
            {trendsAnalytics && (
              <div className="space-y-4">
                <div className="text-left">
                  <h4 className="text-md font-semibold text-[#242424]">
                    Trend Comparison
                  </h4>
                  <p className="text-sm text-[#585755]">
                    Performance vs previous period
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    {
                      ...trendsAnalytics.sessions_trend,
                      title: "Sessions",
                      icon: MessageCircle,
                      color: "primary" as const,
                    },
                    {
                      ...trendsAnalytics.satisfaction_trend,
                      title: "Like Rate",
                      icon: ThumbsUp,
                      color: "success" as const,
                    },
                    {
                      ...trendsAnalytics.user_engagement_trend,
                      title: "User Engagement",
                      icon: Users,
                      color: "purple" as const,
                    },
                  ].map((trend, index) => (
                    <MetricCard
                      key={index}
                      title={trend.title}
                      value={trend.current_value}
                      icon={trend.icon}
                      trend={`${trend.change_percentage > 0 ? "+" : ""}${trend.change_percentage.toFixed(1)}%`}
                      trendDirection={
                        trend.change_direction as "up" | "down" | "stable"
                      }
                      color={trend.color}
                      description={`Previous: ${trend.previous_value.toFixed(2)}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* User Engagement Section */}
            {detailedAnalytics?.user_engagement && detailedAnalytics?.peak_usage_times && (
              <div className="space-y-6">
                <div className="text-left">
                  <h4 className="text-md font-semibold text-[#242424]">User Engagement Patterns</h4>
                  <p className="text-sm text-[#585755]">
                    24-hour usage behavior analysis
                  </p>
                </div>

                {/* User Engagement Metrics - Using DynamicCard */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <DynamicCard
                    title="Active Users"
                    value={detailedAnalytics.user_engagement.active_users.toString()}
                    valueType="text"
                    headerIcon={<Users className="h-4 w-4 text-[#334238]" />}
                    shadow="border"
                    size="sm"
                    showMoreIcon={false}
                  />
                  
                  <DynamicCard
                    title="Sessions/User"
                    value={detailedAnalytics.user_engagement.avg_sessions_per_user.toFixed(1)}
                    valueType="text"
                    headerIcon={<Activity className="h-4 w-4 text-[#334238]" />}
                    shadow="border"
                    size="sm"
                    showMoreIcon={false}
                  />
                  
                  <DynamicCard
                    title="Messages/Session"
                    value={detailedAnalytics.user_engagement.avg_messages_per_session.toFixed(1)}
                    valueType="text"
                    headerIcon={<MessageCircle className="h-4 w-4 text-[#334238]" />}
                    shadow="border"
                    size="sm"
                    showMoreIcon={false}
                  />
                  
                  <DynamicCard
                    title="Returning Users"
                    value={detailedAnalytics.user_engagement.returning_users.toString()}
                    valueType="text"
                    headerIcon={<Clock className="h-4 w-4 text-[#334238]" />}
                    shadow="border"
                    size="sm"
                    showMoreIcon={false}
                  />
                </div>

                {/* Peak Usage Times Chart */}
                {hourlyUsageChartData && (
                  <DynamicCard
                    title="24-Hour Usage Pattern"
                    headerIcon={<Clock className="h-5 w-5 text-[#334238]" />}
                    shadow="border"
                    showMoreIcon={false}
                    customContent={
                      <div className="h-64 w-full">
                        <Bar
                          data={hourlyUsageChartData}
                          options={hourlyUsageChartOptions}
                        />
                      </div>
                    }
                    className="w-full"
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  });

  // Model Card Component
  //
  // Layout:
  //   ┌──────────────────────────────────────────────────┐
  //   │  🧠 deep_analysis                  336 sessions  │   ← header
  //   ├──────────────────────────────────────────────────┤
  //   │  ┌─ Messages ─┐    ┌─ Avg/Session ─┐             │
  //   │  │   273      │    │     0.8       │             │
  //   │  └────────────┘    └───────────────┘             │
  //   └──────────────────────────────────────────────────┘
  //
  // Previous version had:
  //   - a "Like Rate" progress bar between header and stats (always 0%
  //     on this dashboard because feedback is sparse, so it was just
  //     visual noise) — removed.
  //   - a "336 sessions" outline badge below the title — moved to the
  //     top-right of the header so each card has a single, scannable
  //     headline row (model + size).
  //
  // We use the `sections` API rather than the convenience `title` /
  // `badges` props so we can put the session count in the header's
  // right slot (the convenience props render badges below the title).
  const ModelCard = ({ model }: { model: any }) => {
    const avgPerSession =
      model.session_count > 0
        ? (model.message_count / model.session_count).toFixed(1)
        : '0';
    return (
      <DynamicCard
        key={model.model_name}
        shadow="border"
        size="sm"
        // showIcon / showMoreIcon default to TRUE on DynamicCard. Even
        // when using the `sections` API the wrapper CardHeader still
        // rendered the default Diamond icon (left) and ChevronDown
        // (right) around our custom content. Disable both so only the
        // Brain icon + model name + "N sessions" we render below are
        // visible in the header.
        showIcon={false}
        showMoreIcon={false}
        sections={[
          {
            type: 'header',
            content: (
              <div className="flex w-full items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Brain className="h-4 w-4 text-[#334238] shrink-0" />
                  <span className="text-sm font-semibold text-[#242424] truncate">
                    {model.model_name}
                  </span>
                </div>
                <span className="text-xs font-medium text-[#585755] tabular-nums whitespace-nowrap">
                  {model.session_count.toLocaleString()} sessions
                </span>
              </div>
            ),
          },
          {
            type: 'content',
            content: (
              <div className="grid grid-cols-2 gap-2 w-full">
                <DynamicCard
                  title="Messages"
                  value={model.message_count.toString()}
                  valueType="text"
                  shadow="border"
                  size="sm"
                  showIcon={false}
                  showMoreIcon={false}
                  className="flex-1"
                />
                <DynamicCard
                  title="Avg/Session"
                  value={avgPerSession}
                  valueType="text"
                  shadow="border"
                  size="sm"
                  showIcon={false}
                  showMoreIcon={false}
                  className="flex-1"
                />
              </div>
            ),
          },
        ]}
        className="hover:shadow-soft transition-all"
      />
    );
  };

  // Model Performance Analysis - memoized
  const ModelPerformance = memo(() => {
    // Early return if data is still loading
    if (isDataLoading) return null;
    
    if (!detailedAnalytics?.model_performance) return null;

    const models = detailedAnalytics.model_performance.slice(0, 6);

    return (
      <div className="space-y-4 w-full">
        {/* Model Performance Cards */}
        <div className="w-full">
          <h4 className="text-md font-semibold mb-3 text-[#242424]">Model Performance</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 w-full">
            {models.map((model, index) => (
              <ModelCard key={model.model_name} model={model} />
            ))}
          </div>
        </div>
      </div>
    );
  });

  // Brand Card Component
  const BrandCard = ({ brand, index }: { brand: any; index: number }) => {
    // Reserve ~32px on the right for the "#N" rank badge so long brand
    // names like "Home Depot - D27 Electrical & Lighting" don't collide
    // with it. `DynamicCard.title` is typed as a plain string (no JSX),
    // so we truncate visually and expose the full name via a `title`
    // tooltip on the wrapper.
    const fullName = brandMap?.[brand.brand_id] || `Brand ${brand.brand_id}`;
    const MAX_TITLE_CHARS = 22;
    const displayName =
      fullName.length > MAX_TITLE_CHARS
        ? `${fullName.slice(0, MAX_TITLE_CHARS - 1).trimEnd()}…`
        : fullName;
    return (
      <div className="relative" title={fullName}>
        <Badge variant="outline" className="absolute top-3 right-3 z-10 text-xs font-medium">
          #{index + 1}
        </Badge>
        <DynamicCard
          key={brand.brand_id}
          title={displayName}
          headerIcon={<Building className="h-4 w-4 text-[#334238]" />}
          shadow="border"
          size="sm"
          showMoreIcon={false}
          progress={{
            value: brand.satisfaction_rate,
            max: 100,
            label: "Like Rate",
            showPercentage: true,
          }}
          customContent={
            <>
              <div className="grid grid-cols-2 gap-2 w-full mt-3">
                <DynamicCard
                  title="Sessions"
                  value={brand.session_count.toString()}
                  valueType="text"
                  shadow="border"
                  size="sm"
                  showIcon={false}
                  showMoreIcon={false}
                />
                <DynamicCard
                  title="Users"
                  value={brand.user_count.toString()}
                  valueType="text"
                  shadow="border"
                  size="sm"
                  showIcon={false}
                  showMoreIcon={false}
                />
              </div>
              <div className="w-full mt-2">
                <DynamicCard
                  title="Avg Session Length"
                  value={brand.avg_session_length.toFixed(1)}
                  valueType="text"
                  shadow="border"
                  size="sm"
                  showIcon={false}
                  showMoreIcon={false}
                />
              </div>
            </>
          }
          className="hover:shadow-soft transition-all"
        />
      </div>
    );
  };

  // Brand Analytics Deep Dive - memoized
  const BrandAnalytics = memo(() => {
    // Early return if data is still loading
    if (isDataLoading) return null;
    
    if (!detailedAnalytics?.brand_analytics) return null;

    const topBrands = detailedAnalytics.brand_analytics.slice(0, 8);

    return (
      <div className="space-y-4 w-full">
        {/* Brand Performance Section */}
        <div className="w-full">
          <h4 className="text-md font-semibold mb-3 text-[#242424]">Brand Performance Analysis</h4>
          
          {/* Top Brands Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-4 w-full">
            {topBrands.map((brand, index) => (
              <BrandCard key={brand.brand_id} brand={brand} index={index} />
            ))}
          </div>

          {/* Brand Distribution Charts */}
          <div className="grid grid-cols-1 gap-3 w-full">
            <DynamicCard
              title="Session Distribution"
              headerIcon={<BarChart3 className="h-5 w-5 text-[#334238]" />}
              shadow="border"
              size="sm"
              showMoreIcon={false}
              customContent={
                <div className="h-48 w-full -mx-6 -mb-6 px-6 pb-6">
                  <Bar
                    data={{
                      labels: topBrands.map(
                        (b) => brandMap?.[b.brand_id] || `Brand ${b.brand_id}`
                      ),
                      datasets: [
                        {
                          label: "Sessions",
                          data: topBrands.map((b) => b.session_count),
                          backgroundColor: topBrands.map(
                            (_, i) =>
                              Object.values(CHART_COLORS)[
                                i % Object.values(CHART_COLORS).length
                              ] + "80"
                          ),
                          borderColor: topBrands.map(
                            (_, i) =>
                              Object.values(CHART_COLORS)[
                                i % Object.values(CHART_COLORS).length
                              ]
                          ),
                          borderWidth: 2,
                          borderRadius: 4,
                        },
                      ],
                    }}
                    options={{
                      ...STABLE_CHART_OPTIONS,
                      indexAxis: "x" as const,
                      scales: {
                        x: {
                          ticks: { font: { size: 10 }, color: "#585755", maxRotation: 45, minRotation: 0 },
                          grid: { display: false },
                        },
                        y: {
                          beginAtZero: true,
                          ticks: { font: { size: 10 }, color: "#585755", precision: 0 },
                          grid: { color: "#E5E1DF40" },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: "rgba(51, 66, 56, 0.9)",
                          callbacks: {
                            label: (context) => {
                              const total = context.dataset.data.reduce(
                                (a: number, b: any) => a + (b as number),
                                0
                              );
                              const percentage = (
                                ((context.parsed.y ?? 0) / total) *
                                100
                              ).toFixed(1);
                              return `${context.label}: ${context.parsed.y} sessions (${percentage}%)`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              }
              className="w-full"
            />
          </div>
        </div>
      </div>
    );
  });

  // Compact view for embedded use
  if (compact) {
    if (isDataLoading) {
      return (
        <div className="flex items-center justify-center h-32 bg-[#F7F4F2] rounded-xl border border-[#E5E1DF]">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-[#334238]" />
            <div className="text-sm">
              <div className="font-medium text-[#242424]">Loading Analytics...</div>
              <div className="text-xs text-[#585755]">
                Fetching {[
                  !analytics && "overview",
                  !timeSeriesAnalytics && "time series", 
                  !trendsAnalytics && "trends",
                  !detailedAnalytics && "detailed metrics",
                  !brands && "brand data"
                ].filter(Boolean).join(", ")}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <UsageAndTrends />;
  }

  // Full analytics dashboard
  // Use external prop directly - prioritize it over computed activeAnalyticsTab
  // This ensures we always use the latest value from parent
  const currentTab = (externalActiveAnalyticsTab !== undefined && externalActiveAnalyticsTab !== null && externalActiveAnalyticsTab !== "")
    ? externalActiveAnalyticsTab
    : (activeAnalyticsTab || "overview");
  
  // Show content immediately when switching tabs, only show loading on initial load
  const showLoading = loading && !analytics && !detailedAnalytics;
  
  // Use CSS visibility for instant tab switching instead of conditional rendering
  const isOverviewTab = currentTab === "overview";
  const isModelsTab = currentTab === "models";
  
  return (
    <div className="w-full overflow-hidden relative px-4 md:px-6 pb-8">
      {/* Overview Tab - Use CSS to show/hide for instant switching */}
      <div 
        className={`space-y-4 w-full ${isOverviewTab ? 'block' : 'hidden'}`}
        style={{ display: isOverviewTab ? 'block' : 'none' }}
      >
        {showLoading ? (
            <div className="flex items-center justify-center h-48 bg-[#F7F4F2] rounded-xl border border-[#E5E1DF] w-full">
              <div className="flex items-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-[#334238]" />
                <div className="text-center">
                  <div className="text-lg font-medium text-[#242424]">
                    Loading comprehensive analytics...
                  </div>
                  <div className="text-sm text-[#585755] mt-2">
                    Loading {[
                      !analytics && "overview data",
                      !timeSeriesAnalytics && "time series", 
                      !trendsAnalytics && "trend analysis",
                      !detailedAnalytics && "detailed metrics",
                      !brands && "brand information"
                    ].filter(Boolean).join(", ")}
                  </div>
                  <div className="text-xs text-[#A09D9B] mt-1">
                    Please wait while all data sources are synchronized...
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <UsageAndTrends />
          )}
      </div>
      
      {/* Models Tab - Use CSS to show/hide for instant switching */}
      <div 
        className={`space-y-4 w-full ${isModelsTab ? 'block' : 'hidden'}`}
        style={{ display: isModelsTab ? 'block' : 'none' }}
      >
        {showLoading ? (
            <div className="flex items-center justify-center h-48 bg-[#F7F4F2] rounded-xl border border-[#E5E1DF] w-full">
              <div className="flex items-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-[#334238]" />
                <div className="text-center">
                  <div className="text-lg font-medium text-[#242424]">
                    Loading model and brand analytics...
                  </div>
                  <div className="text-sm text-[#585755] mt-2">
                    Preparing performance metrics and insights
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-8 w-full">
              <ModelPerformance />
              <BrandAnalytics />
            </div>
          )}
      </div>
    </div>
  );
};

// Remove memo to ensure component re-renders when activeAnalyticsTab prop changes
export default AnalyticsPanel;
